import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { IoDocumentOutline } from 'react-icons/io5';
import { FiDownload, FiExternalLink } from 'react-icons/fi';
import IconSpinner from '../IconSpinner/IconSpinner';
import { fileExtension, previewKind } from '../../util/fileAttachments';

import css from './AttachmentList.css';

// Sharetribe file attachments as download cards. Shared by the activity feed
// (a message's publicFileAttachments) and the offer modal (a transaction's
// protectedFileAttachments); both are fileAttachment entities with the `file`
// relationship denormalised onto them.

const formatFileSize = bytes => {
  if (!(bytes >= 0)) {
    return null;
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  const rounded = value >= 10 || unitIndex === 0 ? Math.round(value) : value.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
};

// 40px tile shown when an attachment has no image thumbnail: the file
// extension in a brand-tint square, or a generic document icon when the file
// name has no extension.
const AttachmentBadge = ({ name }) => {
  const ext = fileExtension(name);
  return (
    <span className={css.attachmentBadge} aria-hidden="true">
      {ext ? ext.toUpperCase().slice(0, 4) : <IoDocumentOutline />}
    </span>
  );
};

AttachmentBadge.propTypes = {
  name: string.isRequired,
};

// One downloadable attachment card: image thumbnail or extension badge, file
// name + size, an "Open" button for browser-renderable files (image/PDF) and
// a download icon. The whole card downloads the file — an invisible button
// stretched over the card owns the click, and the Open button sits above it
// with its own action (opens the file inline in a new tab). A failed
// thumbnail load (expired signed URL) forces one refetch; if the fresh URL
// fails too, the card falls back to the extension badge.
const AttachmentCard = props => {
  const {
    attachmentId,
    name,
    sizeLabel,
    previewUrl,
    intl,
    downloadInProgress,
    onDownloadAttachment,
    onOpenAttachment,
    onFetchAttachmentPreview,
  } = props;
  const [imgErrorCount, setImgErrorCount] = useState(0);
  const kind = previewKind(name);
  const openLabel = intl.formatMessage({ id: 'ActivityFeed.openFile' }, { name });
  const downloadLabel = intl.formatMessage({ id: 'ActivityFeed.downloadFile' }, { name });

  const showThumb = kind === 'image' && previewUrl && imgErrorCount <= 1;

  const handleImgError = () => {
    if (imgErrorCount === 0) {
      onFetchAttachmentPreview(attachmentId, { force: true });
    }
    setImgErrorCount(count => count + 1);
  };

  return (
    <div className={css.attachmentCard}>
      <button
        type="button"
        className={css.attachmentDownloadArea}
        disabled={downloadInProgress}
        title={downloadLabel}
        aria-label={downloadLabel}
        onClick={() => onDownloadAttachment(attachmentId, name)}
      />
      {showThumb ? (
        <img
          className={css.attachmentThumb}
          src={previewUrl}
          alt=""
          loading="lazy"
          onError={handleImgError}
        />
      ) : (
        <AttachmentBadge name={name} />
      )}
      <span className={css.attachmentInfo}>
        <span className={css.attachmentName}>{name}</span>
        {sizeLabel ? <span className={css.attachmentSize}>{sizeLabel}</span> : null}
      </span>
      {kind ? (
        <button
          type="button"
          className={css.attachmentOpenButton}
          title={openLabel}
          onClick={() => onOpenAttachment(attachmentId, name)}
        >
          <FiExternalLink />
          <span>
            <FormattedMessage id="ActivityFeed.openButton" />
          </span>
        </button>
      ) : null}
      <span className={css.attachmentDownloadIcon} aria-hidden="true">
        {downloadInProgress ? <IconSpinner /> : <FiDownload />}
      </span>
    </div>
  );
};

AttachmentCard.propTypes = {
  attachmentId: string.isRequired,
  name: string.isRequired,
  sizeLabel: string,
  previewUrl: string,
  intl: intlShape.isRequired,
  downloadInProgress: bool.isRequired,
  onDownloadAttachment: func.isRequired,
  onOpenAttachment: func.isRequired,
  onFetchAttachmentPreview: func.isRequired,
};

// Files pending the malware scan are shown as non-clickable "checking" chips;
// only available files can be downloaded.
const AttachmentList = props => {
  const {
    className,
    attachments: allAttachments,
    intl,
    onDownloadAttachment,
    downloadAttachmentInProgressIds,
    onOpenAttachment,
    onFetchAttachmentPreview,
    attachmentPreviewUrls,
  } = props;
  const attachments = (allAttachments || []).filter(
    a => a && !a.attributes.deleted && a.file && !a.file.attributes.deleted
  );

  // Available image attachments still missing a signed preview URL. The
  // fetch thunk no-ops on cached and in-flight ids, so re-runs are cheap.
  const imageIdsNeedingUrl = attachments
    .filter(a => {
      const { name, state } = a.file.attributes;
      return state === 'available' && previewKind(name) === 'image';
    })
    .map(a => a.id.uuid)
    .filter(id => !attachmentPreviewUrls[id]);
  const imageIdsKey = imageIdsNeedingUrl.join(',');

  useEffect(() => {
    imageIdsNeedingUrl.forEach(id => onFetchAttachmentPreview(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIdsKey]);

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className={classNames(css.attachments, className)}>
      {attachments.map(attachment => {
        const attachmentId = attachment.id.uuid;
        const { name, size, state } = attachment.file.attributes;
        const sizeLabel = formatFileSize(size);

        if (state === 'available') {
          return (
            <AttachmentCard
              key={attachmentId}
              attachmentId={attachmentId}
              name={name}
              sizeLabel={sizeLabel}
              previewUrl={attachmentPreviewUrls[attachmentId]}
              intl={intl}
              downloadInProgress={downloadAttachmentInProgressIds.includes(attachmentId)}
              onDownloadAttachment={onDownloadAttachment}
              onOpenAttachment={onOpenAttachment}
              onFetchAttachmentPreview={onFetchAttachmentPreview}
            />
          );
        }

        const isFailed = state === 'verificationFailed';
        return (
          <div
            key={attachmentId}
            className={classNames(css.attachmentCard, css.attachmentCardDisabled, {
              [css.attachmentFailed]: isFailed,
            })}
          >
            <AttachmentBadge name={name} />
            <span className={css.attachmentInfo}>
              <span className={css.attachmentName}>{name}</span>
              <span className={css.attachmentState}>
                {isFailed ? null : <span className={css.attachmentSpinner} aria-hidden="true" />}
                <FormattedMessage
                  id={
                    isFailed
                      ? 'ActivityFeed.attachmentScanFailed'
                      : 'ActivityFeed.attachmentScanning'
                  }
                />
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

AttachmentList.defaultProps = {
  className: null,
  attachments: [],
};

AttachmentList.propTypes = {
  className: string,
  attachments: arrayOf(object),
  intl: intlShape.isRequired,
  onDownloadAttachment: func.isRequired,
  downloadAttachmentInProgressIds: arrayOf(string).isRequired,
  onOpenAttachment: func.isRequired,
  onFetchAttachmentPreview: func.isRequired,
  attachmentPreviewUrls: object.isRequired,
};

export default AttachmentList;
